import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import ImageWithFallback from '@/components/ImageWithFallback';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { FolderKanban } from 'lucide-react';
import { seoData } from '@/utils/seoData';

const ProjectsPage = () => {
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    tr: {
      title: 'Projelerimiz',
      subtitle: 'Başarıyla tamamladığımız endüstriyel otomasyon projeleri',
      loading: 'Yükleniyor...',
    },
    en: {
      title: 'Our Projects',
      subtitle: 'Successfully completed industrial automation projects',
      loading: 'Loading...',
    },
  };

  const t = content[language];
  const pageSeo = seoData.projects[language];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.title}
          </h1>
          <p className="text-xl text-gray-600">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t.loading}</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12">
              <FolderKanban className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Henüz proje eklenmemiş</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div key={project.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="h-48 overflow-hidden">
                    <ImageWithFallback
                      src={project.image_url}
                      alt={language === 'tr' ? project.title_tr : project.title_en}
                      fallbackType="project"
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {language === 'tr' ? project.title_tr : project.title_en}
                    </h3>
                    <p className="text-gray-600 line-clamp-3">
                      {language === 'tr' ? project.description_tr : project.description_en}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
